import React, { useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { useParams } from "react-router-dom";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { UTCToJapanTime } from "common/helper/time.helper";
import { ConversationInquiryTypeEnum } from "common/enums/ConversationInquiryType.enum";
import { ConversationInquiryStatusEnum } from "common/enums/ConversationInquiryStatus.enum";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import CustomConfirmModal from "components/CustomConfirmModal";
import { useCommonContext } from "contexts/CommonContext";
type ChatMessageType = {
  id: number;
  content: string;
  conversation_id: number;
  send_at: string;
  sender_name: string;
  sender_type: number;
};
const ConversationInquiryDetailPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const [conversation, setConversation] = useState<any>(null);
  const [messageText, setMessageText] = useState<string>("");
  const { userData } = useSelector((state: RootState) => state.auth);
  const { conversationInquiryId } = useParams();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  if (!conversationInquiryId) return <></>;

  useEffect(() => {
    const fetchChatMessages = () => {
      axiosInstance
        .get(API_ENDPOINTS.CONVERSATION_INQUIRY + "/" + conversationInquiryId)
        .then((response) => {
          setConversation(response.data);
        })
        .catch((error) => {
          console.error("Error fetching chat messages:", error);
        });
    };

    fetchChatMessages();
    const intervalId = setInterval(fetchChatMessages, 3000);
    setHeaderTitle("お問い合わせ");
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const conversationType = (type: number | undefined) => {
    switch (Number(type)) {
      case ConversationInquiryTypeEnum.ABOUT_LISTING:
        return "出品について";
      case ConversationInquiryTypeEnum.ABOUT_TRANSACTION:
        return "取引について";
      case ConversationInquiryTypeEnum.ABOUT_ISSUE:
        return "不具合について";
      case ConversationInquiryTypeEnum.OTHERS:
        return "その他";
      default:
        return "____";
    }
  };

  const conversationStatus = (status: number | undefined) => {
    switch (status) {
      case ConversationInquiryStatusEnum.UNRESOLVED:
        return "未対応";
      case ConversationInquiryStatusEnum.PROCESSING:
        return "対応中";
      case ConversationInquiryStatusEnum.INQUIRY_CLOSED:
        return "お問い合わせ終了";
      default:
        return "____";
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) {
      message.warning("内容を入力してください");
      return;
    }

    try {
      const dataSend =
        conversation?.data.messages.length === 1
          ? {
              status: ConversationInquiryStatusEnum.PROCESSING,
              content: messageText,
            }
          : {
              content: messageText,
            };

      await axiosInstance
        .put(
          API_ENDPOINTS.CONVERSATION_INQUIRY + "/" + conversationInquiryId,
          dataSend,
        )
        .then((response) => {
          setConversation(response.data);
        })
        .catch((error) => {
          console.error("Error fetching chat messages:", error);
        });

      setMessageText("");
    } catch (error) {
      message.error("Send message error");
    }
  };

  const handleEndConversation = async () => {
    axiosInstance
      .put(API_ENDPOINTS.CONVERSATION_INQUIRY + "/" + conversationInquiryId, {
        status: ConversationInquiryStatusEnum.INQUIRY_CLOSED,
      })
      .then((response) => {
        if (response?.status === 200) {
          setConversation(response.data);
          setOpenConfirmModal(false);
          message.success("お問い合わせを終了しました");
        }
      });
  };

  const createdAtFormated = (utcTime: string) => {
    if (!utcTime) {
      return "";
    }
    const { year, month, day, hour, minute } = UTCToJapanTime(utcTime);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const updatedAtFormated = (messages: any) => {
    if (!messages || messages.length === 0) {
      return "";
    }

    const lastMessage = messages.reduce((latest: any, current: any) => {
      if (!latest) return current;
      return new Date(current.send_at) > new Date(latest.send_at)
        ? current
        : latest;
    }, null);

    if (!lastMessage || !lastMessage.send_at) {
      return "";
    }

    const { year, month, day, hour, minute } = UTCToJapanTime(
      lastMessage.send_at,
    );
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const renderChatHtml = (data: any) => {
    if (data.data === null) {
      return (
        <div className="border border-black w-full h-[100px] flex justify-center items-center">
          No Message
        </div>
      );
    }

    return data.data.messages.map((messageData: ChatMessageType) => {
      const timeJapan = UTCToJapanTime(messageData.send_at);
      if (messageData.sender_type === userData.type) {
        return (
          <div
            key={messageData.id}
            className="self-stretch bg-white flex flex-col justify-start items-end gap-2.5"
          >
            {primaryChatHtml(
              messageData.sender_name,
              messageData.content,
              `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`,
            )}
          </div>
        );
      }

      return (
        <div
          key={messageData.id}
          className="self-stretch bg-white flex flex-col justify-start items-start gap-2.5"
        >
          {secondaryChatHtml(
            messageData.sender_name,
            messageData.content,
            `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`,
          )}
        </div>
      );
    });
  };

  const primaryChatHtml = (
    companyName: string,
    message: string,
    time: string,
  ) => {
    return (
      <>
        <div className="text-right justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
          {companyName}
        </div>
        <div className="w-full md:w-[660px] p-5 bg-primary-light rounded-tl-xl rounded-bl-xl rounded-br-xl flex flex-col justify-start items-start gap-3.5 overflow-hidden">
          <div
            style={{ whiteSpace: "pre-wrap" }}
            className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
          >
            {message}
          </div>
          <div className="self-stretch text-right justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
            {time}
          </div>
        </div>
      </>
    );
  };

  const secondaryChatHtml = (
    companyName: string,
    message: string,
    time: string,
  ) => {
    return (
      <>
        <div className="text-right justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
          {companyName}
        </div>
        <div className="w-full md:w-[660px] p-5 bg-[#F5F5F5] rounded-tr-xl rounded-bl-xl rounded-br-xl flex flex-col justify-start items-start gap-3.5 overflow-hidden">
          <div
            style={{ whiteSpace: "pre-wrap" }}
            className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
          >
            {message}
          </div>
          <div className="self-stretch text-right justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
            {time}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "お問い合わせ",
            link: "/seller/conversation-inquiry",
          },
          {
            label: `お問い合わせ詳細（ID：#${conversation?.data?.code}）`,
            isCurrent: true,
          },
        ]}
      />
      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              お問い合わせID
            </div>
            <div className="flex-1 p-4">#{conversation?.data.code}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              お問い合わせ種別
            </div>
            <div className="flex-1 p-4">
              {conversationType(conversation?.data.type)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              件名
            </div>
            <div className="flex-1 p-4">{conversation?.data.subject}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              お問い合わせ日
            </div>
            <div className="flex-1 p-4">
              {conversationStatus(conversation?.data.status)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ステータス
            </div>
            <div className="flex-1 p-4">
              {createdAtFormated(
                conversation?.data ? conversation?.data.created_at : "",
              )}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              最終更新日
            </div>
            <div className="flex-1 p-4">
              {updatedAtFormated(conversation?.data?.messages)}
            </div>
          </div>
        </div>
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-7">
        <div className="self-stretch flex flex-col justify-start items-start gap-7">
          {conversation &&
            renderChatHtml({
              data: conversation?.data,
            })}
        </div>
      </div>
      {conversation?.data.status !==
      ConversationInquiryStatusEnum.INQUIRY_CLOSED ? (
        <>
          {conversation?.data.status ===
          ConversationInquiryStatusEnum.PROCESSING ? (
            <div className="flex gap-[10px] justify-center flex-col w-full items-center">
              <Button
                onClick={() => setOpenConfirmModal(true)}
                className="flex w-[340px] min-h-[60px] justify-center items-center gap-[10px] rounded-md border-2 font-bold border-primary text-primary bg-white"
              >
                <span>
                  解決したため <br />
                  お問い合わせを終了する
                </span>
              </Button>
              <p className="text-[12px] text-center">
                一度お問い合わせを終了すると、その後は新しいメッセージを送ることができません。
                <br />
                必ず問題が解決していることをご確認のうえ、「解決したためお問い合わせを終了する」ボタンをクリックしてください。
              </p>
            </div>
          ) : (
            ""
          )}

          <div className="self-stretch flex flex-col justify-start items-start gap-[5px]">
            <Input.TextArea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="チャットを送る"
              className="self-stretch h-48 p-5 bg-white rounded-md border border-neutral-border resize-none text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
              maxLength={1000}
              showCount={{
                formatter: ({ count, maxLength }) => `${count} / ${maxLength}`,
              }}
            />
          </div>
          <div className="self-stretch flex flex-col justify-start items-center gap-2.5">
            <Button
              type="primary"
              onClick={sendMessage}
              disabled={!messageText.trim()}
              className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md text-[16px] md:text-lg font-bold font-['Noto_Sans_JP'] leading-7"
            >
              送信する
            </Button>
          </div>
        </>
      ) : (
        <div className="self-stretch flex flex-col justify-start items-center gap-2.5">
          <button
            disabled
            className="w-80 min-h-14 px-5 py-2.5 text-white font-bold bg-[#D5D5D5] rounded-[6px] text-[16px] md:text-[18px] leading-7"
          >
            このお問い合わせは終了しました
          </button>
          <p className="text-[11px] sm:text-[12px] sm:text-center">
            このお問い合わせは終了しています。新たにメッセージを送ることはできません。
            <br />
            引き続きサポートが必要な場合は、本お問い合わせIDを記載のうえ、あらためて新規お問い合わせをお願いいたします。
          </p>
        </div>
      )}

      <CustomConfirmModal
        open={openConfirmModal}
        title={`お問い合わせを
        終了してもよろしいですか？`}
        description={`一度お問い合わせを終了すると、
                      その後は新しいメッセージを送ることができません。
                      必ず問題が解決していることをご確認のうえ、
                      「お問い合わせを終了する」ボタンをクリックしてください。\n
                      ※この操作は取り消すことができません。
                      ※終了後は「過去のお問い合わせ」として履歴に残ります。
          `}
        onOk={handleEndConversation}
        onCancel={() => setOpenConfirmModal(false)}
        okText="お問い合わせを終了する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </div>
  );
};

export default ConversationInquiryDetailPage;
